import React, { useState, useEffect } from 'react';
import { collection, addDoc, query, where, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';

const HelpSupport = () => {
  const { currentUser } = useAuth();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const [newCase, setNewCase] = useState({
    subject: '',
    description: '',
    category: '',
    priority: 'Medium'
  });

  const categories = [
    'Account Issues',
    'Payment & Billing',
    'Technical Support',
    'Session Problems',
    'Profile Management',
    'Feature Request',
    'Bug Report',
    'Other'
  ];

  const priorities = ['Low', 'Medium', 'High', 'Critical'];

  const statusColors = {
    'Open': { bg: '#e3f2fd', color: '#1976d2' },
    'In Progress': { bg: '#fff3cd', color: '#856404' },
    'Waiting for Customer': { bg: '#f8d7da', color: '#721c24' },
    'Resolved': { bg: '#d4edda', color: '#155724' },
    'Closed': { bg: '#f8f9fa', color: '#6c757d' }
  };


  useEffect(() => {
    fetchCases();
  }, [currentUser]);

  const fetchCases = async () => {
    if (!currentUser) {
      setLoading(false);
      return;
    }

    try {
      console.log('Fetching support cases for user:', currentUser.uid);
      
      // Try without orderBy first in case composite index isn't set up
      const casesQuery = query(
        collection(db, 'supportCases'),
        where('userId', '==', currentUser.uid)
      );
      
      const snapshot = await getDocs(casesQuery);
      console.log('Found', snapshot.size, 'support cases');
      
      const casesData = [];
      snapshot.forEach(doc => {
        console.log('Support case:', doc.id, doc.data());
        casesData.push({ id: doc.id, ...doc.data() });
      });
      
      // Sort manually by createdAt
      casesData.sort((a, b) => {
        const aTime = a.createdAt?.toDate?.() || new Date(a.createdAt) || new Date(0);
        const bTime = b.createdAt?.toDate?.() || new Date(b.createdAt) || new Date(0);
        return bTime - aTime;
      });
      
      setCases(casesData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching support cases:', error);
      setCases([]);
      setLoading(false);
    }
  };

  const handleSubmitCase = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setMessage('');

    if (!newCase.subject.trim() || !newCase.description.trim() || !newCase.category) {
      setError('Please fill in all required fields');
      setSubmitting(false);
      return;
    }

    try {
      const caseData = {
        userId: currentUser.uid,
        userEmail: currentUser.email,
        subject: newCase.subject.trim(),
        description: newCase.description.trim(),
        category: newCase.category,
        priority: newCase.priority,
        status: 'Open',
        createdAt: new Date(),
        updatedAt: new Date(),
        caseNumber: `CS-${new Date().getFullYear()}-${String(Date.now()).slice(-6)}`
      };

      const docRef = await addDoc(collection(db, 'supportCases'), caseData);
      
      // Add the new case to the local state with the generated ID
      const newCaseWithId = {
        id: docRef.id,
        ...caseData
      };

      setCases(prev => [newCaseWithId, ...prev]);
      setMessage('Support case created successfully! Our team will review it and get back to you soon.');
      setShowCreateForm(false);
      setNewCase({
        subject: '',
        description: '',
        category: '',
        priority: 'Medium'
      });
    } catch (error) {
      console.error('Error creating support case:', error);
      
      // More detailed error handling
      if (error.code === 'permission-denied') {
        setError('Permission denied. Please make sure you are logged in and try again.');
      } else if (error.code === 'unavailable') {
        setError('Service temporarily unavailable. Please try again later.');
      } else {
        setError(`Failed to create support case: ${error.message}`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (field, value) => {
    setNewCase(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const formatDate = (date) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const getStatusStyle = (status) => {
    return statusColors[status] || { bg: '#f8f9fa', color: '#6c757d' };
  };

  if (loading) {
    return <div className="loading">Loading support cases...</div>;
  }

  return (
    <div className="help-support">
      <div className="support-container">
        <div className="support-header">
          <h2>Help & Support</h2>
          <p>Get help with your account or report issues</p>
        </div>

        {message && (
          <div className="success-message">
            {message}
          </div>
        )}

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <div className="support-actions">
          <button 
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="create-case-btn"
          >
            {showCreateForm ? 'Cancel' : '+ Create New Case'}
          </button>
        </div>

        {showCreateForm && (
          <div className="create-case-form">
            <h3>Create New Support Case</h3>
            <form onSubmit={handleSubmitCase}>
              <div className="form-row">
                <div className="form-group">
                  <label>Subject *</label>
                  <input
                    type="text"
                    value={newCase.subject}
                    onChange={(e) => handleInputChange('subject', e.target.value)}
                    placeholder="Brief description of your issue"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Category *</label>
                  <select
                    value={newCase.category}
                    onChange={(e) => handleInputChange('category', e.target.value)}
                    required
                  >
                    <option value="">Select a category</option>
                    {categories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Priority</label>
                  <select
                    value={newCase.priority}
                    onChange={(e) => handleInputChange('priority', e.target.value)}
                  >
                    {priorities.map(priority => (
                      <option key={priority} value={priority}>{priority}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Description *</label>
                <textarea
                  value={newCase.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Please provide detailed information about your issue..."
                  rows="5"
                  required
                />
              </div>

              <div className="form-actions">
                <button type="submit" disabled={submitting} className="submit-case-btn">
                  {submitting ? 'Creating Case...' : 'Create Case'}
                </button>
                <button 
                  type="button" 
                  onClick={() => setShowCreateForm(false)}
                  className="cancel-btn"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="cases-section">
          <h3>Your Support Cases</h3>
          
          {cases.length === 0 ? (
            <div className="no-cases">
              <div className="no-cases-icon">📞</div>
              <h4>No Support Cases</h4>
              <p>You haven't created any support cases yet. If you need help, feel free to create a new case!</p>
            </div>
          ) : (
            <div className="cases-list">
              {cases.map(supportCase => (
                <div key={supportCase.id} className="case-card">
                  <div className="case-header">
                    <div className="case-info">
                      <div className="case-number">#{supportCase.caseNumber}</div>
                      <div className="case-subject">{supportCase.subject}</div>
                    </div>
                    <div className="case-status">
                      <span 
                        className="status-badge"
                        style={{
                          backgroundColor: getStatusStyle(supportCase.status).bg,
                          color: getStatusStyle(supportCase.status).color
                        }}
                      >
                        {supportCase.status}
                      </span>
                    </div>
                  </div>

                  <div className="case-meta">
                    <div className="case-category">
                      <strong>Category:</strong> {supportCase.category}
                    </div>
                    <div className="case-priority">
                      <strong>Priority:</strong> 
                      <span className={`priority priority-${supportCase.priority.toLowerCase()}`}>
                        {supportCase.priority}
                      </span>
                    </div>
                  </div>

                  <div className="case-description">
                    {supportCase.description}
                  </div>

                  {supportCase.resolution && supportCase.resolution.trim() && (
                    <div className="case-resolution">
                      <strong>Resolution:</strong>
                      <div className="resolution-content">
                        {supportCase.resolution}
                      </div>
                    </div>
                  )}

                  <div className="case-dates">
                    <div className="case-created">
                      <strong>Created:</strong> {formatDate(supportCase.createdAt)}
                    </div>
                    <div className="case-updated">
                      <strong>Last Updated:</strong> {formatDate(supportCase.updatedAt)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HelpSupport;